import React, { useState } from 'react';

import { NextPageWithLayout } from 'next';
import NextImage from 'next/image';
import ContentLoader from 'react-content-loader';

import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { AppStoreButtons } from '../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Button } from '../../components/Clickables/Buttons/Button';
import { Container } from '../../components/Container/Container';
import { FishingWaterCarousel } from '../../components/FishingWaterCarousel/FishingWaterCarousel';
import { Heading } from '../../components/Heading/Heading';
import { ChevronDownIcon } from '../../components/Icons/ChevronDownIcon';
import { ChevronUpIcon } from '../../components/Icons/ChevronUpIcon';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { QRCode } from '../../components/QRCode/QRCode';
import { Rating, RatingVariant } from '../../components/Rating/Rating';
import { SEOLinksFooter } from '../../components/SEOFooter/SEOLinksFooter';
import { Text } from '../../components/Text/Text';
import { IOS_APP_RATING } from '../../constants/constants';
import { useClientHasRendered } from '../../helpers/hooks';
import {
  GetMostActiveWaters_statistics_mostActiveWaters,
  GetMostActiveWaters_statistics_mostActiveWatersLastWeek,
} from '../../interfaces/graphql';

import BGDesktop from './assets/BGDesktop.png';
import BGMobile from './assets/BGMobile.png';

export interface IPopularFishingWatersProps {
  allTimeWaters: GetMostActiveWaters_statistics_mostActiveWaters[];
  lastWeekWaters: GetMostActiveWaters_statistics_mostActiveWatersLastWeek[];
}

export const PopularFishingWaters: NextPageWithLayout<IPopularFishingWatersProps> = ({
  allTimeWaters,
  lastWeekWaters,
}: IPopularFishingWatersProps) => {
  const [showAll, setShowAll] = useState(false);
  const clientHasRendered = useClientHasRendered();

  return (
    <div>
      <MetaTags
        title="Popular Fishing Waters"
        link="/fishing-waters/popular"
        description="Browse our most popular fishing spots of all-time, or see which spots have been the most popular over the last 7 days."
      />
      <Container className="-px-6 sm:-px-11 md:-px-14 grid w-full grid-cols-1 grid-rows-1">
        <div className="col-start-1 row-start-1">
          <NextImage
            alt="most liked catch image"
            className="size-full object-cover sm:hidden"
            src={BGMobile}
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <NextImage
            alt="most liked catch image"
            className="hidden size-full object-cover sm:block"
            src={BGDesktop}
            placeholder="blur"
            sizes="(min-width: 1680px) 1552px, 100vw"
          />
        </div>
        <div className="col-start-1 row-start-1 flex flex-col items-center justify-center px-2 py-8 text-center sm:w-2/3 sm:pt-4 sm:pb-2 sm:pl-4 md:w-3/5 md:pt-8 md:pl-12">
          <Heading as="h1" variant="lg" className="text-sea-foam relative mt-0 mb-4">
            Explore Fishbrain&apos;s most popular Fishing Waters
          </Heading>
          <Text className="text-sea-foam px-12">
            Get the full experience by downloading the app
          </Text>
          {clientHasRendered ? (
            <div className="mt-3 flex flex-col md:flex-row md:gap-x-6">
              <QRCode source="PopularFishingWaters" />
              <div className="flex flex-col items-center">
                <div className="flex justify-start">
                  <AppStoreButtons source="PopularFishingWaters" orientation="vertical" />
                </div>
                <Rating
                  rating={IOS_APP_RATING}
                  variant={RatingVariant.allStars}
                  textSize="sm"
                  reviewPointColor="text-sea-foam"
                />
                <Text variant="sm" className="text-sea-foam mt-2 font-semibold">
                  37.6K ratings
                </Text>
              </div>
            </div>
          ) : (
            <ContentLoader
              uniqueKey="discover-species-rating-loader-key"
              speed={2}
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
              height={113}
              width={160}
            >
              <rect x="0" y="0" width="400" height="250" />
            </ContentLoader>
          )}
        </div>
      </Container>
      <Container className="mt-8">
        <Text className={` ${showAll ? '' : 'line-clamp-4 break-words'}`}>
          <span className="mb-2 block">
            Fishbrain tracks data on fishing spots around the world. Below are the waters with the
            most catches logged this week and all time. Want to get your favorite spot at the top of
            the list? Get your friends to download Fishbrain and hit up your favorite fishing spot
            to log your catches.
          </span>
          <span>
            Browse the popular locations below to learn more about what species were caught, what
            bait and gear was used to reel in the most catches, and more.
          </span>
        </Text>
        {showAll ? (
          <div className="flex justify-center py-2">
            <Button
              variant="textPrimary"
              onClick={() => {
                setShowAll(false);
              }}
            >
              <ChevronUpIcon />
              <span className="sr-only">Show less</span>
            </Button>
          </div>
        ) : (
          <div className="flex justify-center py-2">
            <Button
              variant="textPrimary"
              onClick={() => {
                setShowAll(true);
              }}
            >
              <ChevronDownIcon />
              <span className="sr-only">Show more</span>
            </Button>
          </div>
        )}
      </Container>

      <FishingWaterCarousel
        dataTestId="last-week"
        title="Most Active Fishing spots (Last week)"
        clickEventSource="popular-last-week"
        waters={lastWeekWaters}
        outerClass="mt-sm!"
        priorityLoad
      />
      <FishingWaterCarousel
        dataTestId="all-time"
        title="Most Active Fishing spots (All-time)"
        clickEventSource="popular-all-time"
        waters={allTimeWaters}
        outerClass="mb-sm"
      />
      {/* TODO: https://fishbrain.atlassian.net/browse/FIB-41168 */}
      <AppDownloadSection hasAppRating />
      <SEOLinksFooter />
    </div>
  );
};
